import { useEffect, useRef, useState } from "react"
import { usePage } from "@inertiajs/react"
import { BarChart3 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  getAnalyticsConsent,
  onAnalyticsConsentChange,
  OPEN_ANALYTICS_PREFERENCES_EVENT,
  setAnalyticsConsent,
} from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

type Locale = "en" | "de"

const COPY = {
  en: {
    title: "Help us improve this field guide",
    description:
      "With your permission, PostHog measures page use, scrolling and clicks, and records masked session replays. Form entries and precise location are not recorded.",
    allow: "Allow analytics",
    necessary: "Only necessary",
    later: "You can change this choice later in the header or footer.",
    preferences: "Analytics settings",
  },
  de: {
    title: "Hilf uns, diesen Reiseführer zu verbessern",
    description:
      "Mit deiner Zustimmung misst PostHog Seitenaufrufe, Scrollen und Klicks und zeichnet maskierte Sitzungswiedergaben auf. Formulareingaben und dein genauer Standort werden nicht erfasst.",
    allow: "Analyse erlauben",
    necessary: "Nur notwendige",
    later: "Du kannst diese Auswahl später in der Kopf- oder Fußzeile ändern.",
    preferences: "Analyse-Einstellungen",
  },
} satisfies Record<Locale, Record<string, string>>

export function AnalyticsConsentBanner({ locale }: { locale: Locale }) {
  const { props } = usePage<PageProps>()
  const configured = props.analytics !== null
  const [open, setOpen] = useState(false)
  const allowButtonRef = useRef<HTMLButtonElement>(null)
  const copy = COPY[locale]

  useEffect(() => {
    if (!configured) return

    setOpen(getAnalyticsConsent() === null)
    const stopListeningForConsent = onAnalyticsConsentChange(() => setOpen(false))
    const openPreferences = () => setOpen(true)
    window.addEventListener(OPEN_ANALYTICS_PREFERENCES_EVENT, openPreferences)

    return () => {
      stopListeningForConsent()
      window.removeEventListener(OPEN_ANALYTICS_PREFERENCES_EVENT, openPreferences)
    }
  }, [configured])

  useEffect(() => {
    if (!open) return

    const frame = window.requestAnimationFrame(() => allowButtonRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [open])

  if (!configured || !open) return null

  return (
    <section
      role="region"
      aria-labelledby="analytics-consent-title"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-xl bg-accent-hover p-4 text-surface md:bottom-5 md:flex md:items-center md:gap-6 md:p-5"
    >
      <div className="flex min-w-0 flex-1 gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface/10 text-surface" aria-hidden>
          <BarChart3 className="h-4 w-4" />
        </span>
        <div>
          <h2 id="analytics-consent-title" className="analytics-consent-title">{copy.title}</h2>
          <p className="analytics-consent-copy mt-1">{copy.description}</p>
          <p className="analytics-consent-note mt-1">{copy.later}</p>
        </div>
      </div>
      <div className="mt-4 flex shrink-0 flex-col gap-2 xs:flex-row md:mt-0">
        <Button ref={allowButtonRef} type="button" onClick={() => setAnalyticsConsent(true)} className="bg-surface text-accent-hover hover:bg-surface-tinted">
          {copy.allow}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setAnalyticsConsent(false)} className="border-surface/35 bg-transparent text-surface hover:bg-surface/10">
          {copy.necessary}
        </Button>
      </div>
    </section>
  )
}

export function AnalyticsPreferencesButton({ locale, compact = false }: { locale: Locale; compact?: boolean }) {
  const { props } = usePage<PageProps>()
  const copy = COPY[locale]

  if (props.analytics === null) return null

  if (compact) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={copy.preferences}
        title={copy.preferences}
        onClick={() => window.dispatchEvent(new Event(OPEN_ANALYTICS_PREFERENCES_EVENT))}
        className="h-9 w-9 rounded-full border border-hairline bg-surface text-ink-secondary hover:bg-surface-subtle"
      >
        <BarChart3 className="h-4 w-4" aria-hidden />
      </Button>
    )
  }

  return (
    <Button
      type="button"
      variant="link"
      onClick={() => window.dispatchEvent(new Event(OPEN_ANALYTICS_PREFERENCES_EVENT))}
      className="justify-start text-left text-sm text-ink-muted hover:text-ink-display"
    >
      {copy.preferences}
    </Button>
  )
}
